import { ApiClient } from './client';
import type { ActivityDay } from '@/types/gamification.types';

export interface UserStats {
  totalXP: number;
  /** Saldo gastable = XP ganado − XP gastado en recompensas (no afecta al ranking). */
  walletBalance: number;
  storiesCompleted: number;
  currentStreak: number;
  longestStreak: number;
  lastActivityDate: string | null;
}

/** Un intento individual de un reto. Una fila por cada submit. */
export interface StoryAttemptItem {
  id: string;
  score: number;
  timeSpent: number;
  xpEarned: number;
  completedAt: number;
}

export interface StoryHistoryItem {
  id: string;
  storyId: string;
  storyTitle: string;
  storyTitleTranslation?: string | null;
  storyLevel: string;
  storyDescription: string;
  storyDescriptionTranslation?: string | null;
  storyCategory: string | null;
  storyBackgroundImage: string | null;
  storyEstimatedTime: number;
  storyXpReward: number;
  score: number;
  xpEarned: number;
  timeSpent: number;
  completedAt: string;
  passed: boolean;
}

export type { ActivityDay };

export interface WeeklyGoalResponse {
  storiesCompleted: number;
  targetStories: number;
  timeSpentMinutes: number;
  targetMinutes: number;
  xpEarnedThisWeek: number;
  progressStories: number;
  progressTime: number;
}

export interface UpdateUserData {
  level?: string;
  name?: string;
}

export interface UpdatedUser {
  id: string;
  email: string;
  name: string;
  role: string;
  level?: string;
  companyId?: string;
  avatar?: string;
  createdAt: string;
}

export interface StudentObjectives {
  totalStoriesCompleted: number;
  averageAccuracy: number;
  bestStreak: number;
  totalXPEarned: number;
  storiesCompletedThisWeek: number;
  storiesCompletedThisMonth: number;
  /** Media de puntuación (0–100) en resultados del mes en curso. */
  averageAccuracyThisMonth?: number;
  /** Minutos de media por intento de reto en el mes en curso (misma lógica que `averageTimePerStory` global). */
  averageTimePerStoryThisMonth?: number;
  averageTimePerStory: number;
  /** Suma de `timeSpent` (segundos) de todos los resultados de retos del usuario. */
  totalStudyTimeSeconds?: number;
  /** Segundos de estudio del mes en curso (biblioteca + retos/recursos vía agregación diaria). */
  totalStudyTimeSecondsThisMonth?: number;
  strongestCategory: string;
  weakestCategory: string;
}

export interface SaveActivityErrorPayload {
  activityId: string;
  activityType: string;
  sourceType: 'story' | 'resource' | 'conversation';
  sourceId: string;
  userAnswer: unknown;
}

export interface UserActivityError {
  id: string;
  activityId: string;
  activityType: string;
  sourceType: 'story' | 'resource' | 'conversation';
  sourceId: string;
  errorCount: number;
  lastUserAnswer: unknown;
  firstErrorAt: number;  // Unix timestamp ms
  lastErrorAt: number;   // Unix timestamp ms
  interval: number;      // SM-2: days until next review
  easeFactor: number;    // SM-2: ease multiplier
  repetitions: number;   // SM-2: consecutive correct review streak
  nextReviewAt: number | null; // SM-2: Unix ms of next scheduled review
}

export interface GetUserErrorsResponse {
  errors: UserActivityError[];
  total: number;
}

export interface ReviewDueSource {
  sourceType: 'story' | 'resource' | 'conversation';
  sourceId: string;
  sourceTitle: string;
  sourceDescription: string;
  sourceXpReward: number;
  sourceEstimatedTime: number;
  sourceTag: string;
  /** Imagen de portada del reto; en recursos, la del primer reto relacionado si existe */
  sourceBackgroundImage?: string | null;
  dueCount: number;
  activityTypes: string[];
  oldestDue: number; // Unix ms
}

export interface GetReviewDueResponse {
  sources: ReviewDueSource[];
  /** Filas de actividades en cola de repaso (todas las páginas). */
  totalItems: number;
  /** Orígenes distintos (retos/recursos) en cola; para copy y vacío. */
  totalSources: number;
  hasMore: boolean;
}

/**
 * Users API - calls backend /api/users
 */
export interface ObjectiveAchievementRow {
  id: string;
  userId: string;
  storyId: string;
  objectiveId: string;
  canStatement: string;
  skill: string;
  correctCount: number;
  totalCount: number;
  pct: number;
  achieved: boolean;
  completedAt: number;
}

export interface LearnedWord {
  id: string;
  userId: string;
  storyId: string;
  activityId: string;
  word: string;
  partOfSpeech: string | null;
  definition: string;
  example: string | null;
  audioUrl: string | null;
  learnedAt: number;
}

export const usersAPI = {
  getStats: async (): Promise<UserStats> => {
    return ApiClient.get<UserStats>('/users/me/stats');
  },

  /**
   * Historial de historias paginado server-side.
   * `from`/`to` son timestamps ms; `passedOnly` filtra a resultados aprobados (score >= 70).
   * Devuelve `{ items, hasMore }` para que la UI sepa si hay más páginas tras la actual.
   */
  getStoryHistory: async (params?: {
    from?: number;
    to?: number;
    passedOnly?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<{ items: StoryHistoryItem[]; hasMore: boolean }> => {
    const search = new URLSearchParams();
    if (params?.from !== undefined) search.set('from', String(params.from));
    if (params?.to !== undefined) search.set('to', String(params.to));
    if (params?.passedOnly) search.set('passedOnly', 'true');
    if (params?.limit !== undefined) search.set('limit', String(params.limit));
    if (params?.offset !== undefined) search.set('offset', String(params.offset));
    const q = search.toString();
    return ApiClient.get<{ items: StoryHistoryItem[]; hasMore: boolean }>(
      `/users/me/stories/history${q ? `?${q}` : ''}`
    );
  },

  getActivities: async (): Promise<ActivityDay[]> => {
    return ApiClient.get<ActivityDay[]>('/users/me/activities');
  },

  getWeeklyGoal: async (): Promise<WeeklyGoalResponse> => {
    return ApiClient.get<WeeklyGoalResponse>('/users/me/goals/weekly');
  },

  getObjectives: async (): Promise<StudentObjectives> => {
    return ApiClient.get<StudentObjectives>('/users/me/objectives');
  },

  updateMe: async (data: UpdateUserData): Promise<{ user: UpdatedUser }> => {
    return ApiClient.patch<{ user: UpdatedUser }>('/users/me', data);
  },

  getErrors: async (activityType?: string): Promise<GetUserErrorsResponse> => {
    const query = activityType ? `?activityType=${encodeURIComponent(activityType)}` : '';
    return ApiClient.get<GetUserErrorsResponse>(`/users/me/errors${query}`);
  },

  saveActivityError: async (payload: SaveActivityErrorPayload): Promise<void> => {
    await ApiClient.post<{ success: boolean }>('/users/me/activity-error', payload);
  },

  getReviewDue: async (limit?: number, offset?: number): Promise<GetReviewDueResponse> => {
    const params = new URLSearchParams();
    if (limit !== undefined) params.set('limit', String(limit));
    if (offset !== undefined) params.set('offset', String(offset));
    const q = params.toString();
    return ApiClient.get<GetReviewDueResponse>(`/users/me/review-due${q ? `?${q}` : ''}`);
  },

  /**
   * Acumula segundos de estudio de vocabulario (día local en servidor). Solo
   * contabiliza tiempo: NO marca racha. Para marcar racha tras completar una
   * conversación, usar `markConversationCompleted`.
   */
  addVocabularyStudyDelta: async (deltaSeconds: number): Promise<void> => {
    if (deltaSeconds < 1) return;
    await ApiClient.post<{ ok: boolean }>('/users/me/vocabulary/study-seconds', {
      deltaSeconds,
    });
  },

  /**
   * Acumula segundos de estudio de historias y conversaciones en la tabla diaria
   * `ChallengeResourceStudyDay` (día local en servidor). Lo llaman los
   * cronómetros en vivo, así que el tiempo cuenta aunque el alumno cierre sin
   * enviar. El `submit` de historias/conversaciones ya no suma a esta tabla.
   */
  addStoryResourceStudyDelta: async (deltaSeconds: number): Promise<void> => {
    if (deltaSeconds < 1) return;
    await ApiClient.post<{ ok: boolean }>('/users/me/study-seconds', { deltaSeconds });
  },

  /**
   * Marca el día como “conversación completada” para la racha. Idempotente.
   * Llamar solo cuando el alumno responda a todas las preguntas del chat
   * (`turnIdx >= turns.length`). No suma tiempo de estudio.
   */
  markConversationCompleted: async (): Promise<void> => {
    await ApiClient.post<{ ok: true }>('/users/me/conversation/mark-completed');
  },

  getObjectiveAchievements: async (params?: {
    from?: number;
    to?: number;
    skill?: string;
  }): Promise<ObjectiveAchievementRow[]> => {
    const q = new URLSearchParams();
    if (params?.from !== undefined) q.set('from', String(params.from));
    if (params?.to !== undefined) q.set('to', String(params.to));
    if (params?.skill) q.set('skill', params.skill);
    const qs = q.toString();
    return ApiClient.get<ObjectiveAchievementRow[]>(`/users/objective-achievements${qs ? `?${qs}` : ''}`);
  },

  /** Lista de intentos individuales del usuario sobre un reto, ordenada desc. */
  getStoryAttempts: async (storyId: string): Promise<StoryAttemptItem[]> => {
    return ApiClient.get<StoryAttemptItem[]>(
      `/users/me/stories/${encodeURIComponent(storyId)}/attempts`
    );
  },

  getLearnedWords: async (params?: {
    limit?: number;
    offset?: number;
    search?: string;
    from?: number;
    to?: number;
    /** Excluye palabras ya vistas (case-insensitive) antes de este timestamp ms. */
    excludeSeenBefore?: number;
  }): Promise<LearnedWord[]> => {
    const q = new URLSearchParams();
    if (params?.limit !== undefined) q.set('limit', String(params.limit));
    if (params?.offset !== undefined) q.set('offset', String(params.offset));
    if (params?.search) q.set('search', params.search);
    if (params?.from !== undefined) q.set('from', String(params.from));
    if (params?.to !== undefined) q.set('to', String(params.to));
    if (params?.excludeSeenBefore !== undefined)
      q.set('excludeSeenBefore', String(params.excludeSeenBefore));
    const qs = q.toString();
    return ApiClient.get<LearnedWord[]>(`/users/learned-words${qs ? `?${qs}` : ''}`);
  },

  getLearnedWordById: async (wordId: string): Promise<LearnedWord> => {
    return ApiClient.get<LearnedWord>(`/users/learned-words/${encodeURIComponent(wordId)}`);
  },

  getLearnedWordsCount: async (params?: {
    from?: number;
    to?: number;
    /** Excluye palabras (LOWER+TRIM) ya vistas antes de este timestamp ms.
     *  Mismo filtro que `getLearnedWords` para que el count cuadre con el listado. */
    excludeSeenBefore?: number;
  }): Promise<{ count: number }> => {
    const q = new URLSearchParams();
    if (params?.from !== undefined) q.set('from', String(params.from));
    if (params?.to !== undefined) q.set('to', String(params.to));
    if (params?.excludeSeenBefore !== undefined) {
      q.set('excludeSeenBefore', String(params.excludeSeenBefore));
    }
    const qs = q.toString();
    return ApiClient.get<{ count: number }>(`/users/learned-words/count${qs ? `?${qs}` : ''}`);
  },

  /**
   * Serie diaria de tiempo de estudio (segundos por día), biblioteca + historias/recursos.
   * - `this_week`: lunes a domingo de la semana ISO actual (días futuros con 0)
   * - `this_month`: día 1 → último día del mes actual (días futuros con 0)
   * - `days` (opcional): ventana móvil de últimos N días (compat)
   */
  getStudyTimeDaily: async (
    params: { range: 'this_week' | 'this_month' } | { days: number }
  ): Promise<{
    range?: 'this_week' | 'this_month' | 'rolling';
    days: number;
    series: { date: string; seconds: number }[];
  }> => {
    const q = new URLSearchParams();
    if ('range' in params) {
      q.set('range', params.range);
    } else {
      q.set('days', String(params.days));
    }
    const qs = q.toString();
    return ApiClient.get<{
      range?: 'this_week' | 'this_month' | 'rolling';
      days: number;
      series: { date: string; seconds: number }[];
    }>(`/users/me/study-time-daily${qs ? `?${qs}` : ''}`);
  },
};
